import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

// Custom Leaflet layer that draws ALL dots on a single canvas element.
// Massively cheaper than mounting one <CircleMarker> React node per point on
// low-end mobile devices — halves pan/zoom lag on lists with hundreds of pins.
//
// Contract:
//   points  : [{ cid: string, lat: number, lng: number, color: string, radius: number,
//               strokeColor?: string, strokeWidth?: number }]
//   onSelect: (cid, evt) => void — called when the user taps close to a dot.
//   enabled : bool — when false, clicks pass through to other map handlers so
//             "picking mode" (add / move / calon) keeps working.
const _TurboLayer = L.Layer.extend({
  initialize(options) {
    L.setOptions(this, options);
    this._points = options.points || [];
    this._onSelect = options.onSelect;
    this._enabled = options.enabled !== false;
  },
  onAdd(map) {
    this._map = map;
    const canvas = this._canvas = L.DomUtil.create(
      "canvas", "leaflet-turbo-dots-layer leaflet-layer"
    );
    canvas.style.position = "absolute";
    canvas.style.pointerEvents = "none"; // let clicks reach the map — we use map click
    canvas.style.left = "0";
    canvas.style.top = "0";
    // Match the map's default z-order for overlays (above tiles, below markers/popups).
    map.getPanes().overlayPane.appendChild(canvas);
    this._resize();
    map.on("moveend zoomend viewreset resize", this._redraw, this);
    map.on("click", this._onMapClick, this);
    this._redraw();
  },
  onRemove(map) {
    if (this._canvas) L.DomUtil.remove(this._canvas);
    this._canvas = null;
    map.off("moveend zoomend viewreset resize", this._redraw, this);
    map.off("click", this._onMapClick, this);
  },
  setPoints(points) {
    this._points = points || [];
    this._redraw();
  },
  setEnabled(enabled) {
    this._enabled = !!enabled;
  },
  _resize() {
    if (!this._map || !this._canvas) return;
    const size = this._map.getSize();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const canvas = this._canvas;
    // Backing store size (HiDPI). Draw ops are scaled below so `dpr==2` is crisp
    // on retina without doubling paint cost when device is 1x (older phones).
    if (canvas.width !== size.x * dpr || canvas.height !== size.y * dpr) {
      canvas.width = size.x * dpr;
      canvas.height = size.y * dpr;
      canvas.style.width = size.x + "px";
      canvas.style.height = size.y + "px";
    }
    const topLeft = this._map.containerPointToLayerPoint([0, 0]);
    L.DomUtil.setPosition(canvas, topLeft);
    this._dpr = dpr;
  },
  _redraw() {
    if (!this._map || !this._canvas) return;
    this._resize();
    const canvas = this._canvas;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const dpr = this._dpr || 1;
    ctx.scale(dpr, dpr);
    // Skip points outside the visible bounds — the map does this too but we may have
    // been handed a superset (e.g. pre-cluster list). Slight over-draw is fine.
    const bounds = this._map.getBounds();
    const map = this._map;
    for (const p of this._points) {
      if (!bounds.contains([p.lat, p.lng])) continue;
      const cp = map.latLngToContainerPoint([p.lat, p.lng]);
      const r = Math.max(2, Number(p.radius || 6));
      const stroke = p.strokeColor || "#ffffff";
      const strokeWidth = Number(p.strokeWidth || 1.2);
      // Filled colored disc with white outline.
      ctx.beginPath();
      ctx.arc(cp.x, cp.y, r, 0, Math.PI * 2);
      ctx.fillStyle = p.color || "#10b981";
      ctx.fill();
      if (strokeWidth > 0) {
        ctx.lineWidth = strokeWidth;
        ctx.strokeStyle = stroke;
        ctx.stroke();
      }
    }
  },
  _onMapClick(e) {
    if (!this._enabled || !this._points || !this._onSelect) return;
    const map = this._map;
    const clickPt = map.latLngToContainerPoint(e.latlng);
    // 44px Apple HIG / 48dp Material touch target → 22px radius.
    const threshold = 22;
    let best = null;
    let bestDist = threshold * threshold;
    const bounds = map.getBounds();
    for (const p of this._points) {
      if (!bounds.contains([p.lat, p.lng])) continue;
      const cp = map.latLngToContainerPoint([p.lat, p.lng]);
      const dx = cp.x - clickPt.x;
      const dy = cp.y - clickPt.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDist) {
        bestDist = d2;
        best = p;
      }
    }
    if (best) {
      L.DomEvent.stopPropagation(e);
      this._onSelect(best.cid, e);
    }
  },
});

export default function TurboDotsLayer({ points, onSelect, enabled = true }) {
  const map = useMap();
  const layerRef = useRef(null);
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;

  useEffect(() => {
    const layer = new _TurboLayer({
      points: [],
      enabled: true,
      onSelect: (cid, e) => { if (selectRef.current) selectRef.current(cid, e); },
    });
    layer.addTo(map);
    layerRef.current = layer;
    return () => {
      try { map.removeLayer(layer); } catch { /* ignore */ }
      layerRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    if (layerRef.current) layerRef.current.setPoints(points || []);
  }, [points]);

  useEffect(() => {
    if (layerRef.current) layerRef.current.setEnabled(enabled);
  }, [enabled]);

  return null;
}
